/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from "@angular/router";

import { DriverObject } from '../../models/driver-object';
import { DriverObjectInstance } from "../../models/driver-object-instance";
import { EmGroup } from '../../models/em-group';
import { GroupType } from '../../models/group-type';
import { InstanceData } from "../../models/instance-data";

import { DriverObjectService } from "../../services/driver-object.service";
import { GroupService } from "../../services/group.service";

import { AddToGroupDialogComponent } from "../add-to-group-dialog.component";
import { ConfirmDeleteDialogComponent } from "../confirm-delete-dialog.component";
import { ManageGroupsDialogComponent } from "../manage-groups-dialog.component";
import { RemoveFromGroupDialogComponent } from "../remove-from-group-dialog.component";
import { ShowGroupDialogComponent } from "../show-group-dialog.component";

import { PagedResults } from 'app/models/paged-results';
import { NgxSpinnerService } from 'ngx-spinner';
import { EntityInstanceDialogComponent } from './dialogs/entity-instance-dialog.component';
import { EntityTypeDialogComponent } from "./dialogs/entity-type-dialog.component";
import { INFRA_DUMMY_FUNCTION, RESULTS_PER_PAGE } from 'app/app-constants';
import { FunctionParameterDataUi } from './dialogs/general-function-dialog.component';
import { ERROR_DIALOG_TITLE, SimpleSuccessErrorDialogComponent } from '../simple-success-error-dialog-box/simple-success-error-dialog-box.component';

//export const PageSize_InfrastructureTagsPage = 10;

/** Filter Option representing the "REMOVE FILTER" Option */
const FilterOption_RemoveFilter = { column: null, label: "REMOVE FILTER" } as FilterOption;

@Component({
  templateUrl: './driver-objects.component.html',
  styleUrls: ['../../../assets/css/infrastructure.css']
})
export class DriverObjectsComponent implements OnInit {

  @ViewChild('confirmDeleteDialog')
  mConfirmDeleteDialog: ConfirmDeleteDialogComponent;

  @ViewChild('showGroupDialog')
  mShowGroupDialog: ShowGroupDialogComponent;

  @ViewChild('manageGroupsDialog')
  mManageGroupsDialog: ManageGroupsDialogComponent;

  @ViewChild('addToGroupDialog')
  mAddToGroupDialogComponent: AddToGroupDialogComponent;

  @ViewChild('removeFromGroupDialog')
  mRemoveFromGroupDialogComponent: RemoveFromGroupDialogComponent;

  @ViewChild('entityTypeDialog')
  mEntityTypeDialogComponent: EntityTypeDialogComponent;

  @ViewChild('entityInstanceDialog')
  mEntityInstanceDialogComponent: EntityInstanceDialogComponent;

  /** Reference to the SimpleSuccessErrorDialog used to display error */
@ViewChild('successErrorDialog') 
simpleSuccessErrorDialogComponent: SimpleSuccessErrorDialogComponent;

  mDriverObjects: DriverObjectUI[] = [];
  mDriverObjectInstances: DriverObjectInstanceUI[] = [];

  mDeleteFlag = 0; //1 = Tag Definitions, 2 = Tag Instances


  //Entity Type Dialog Variables
  mEntity: DriverObject;
  mEntityName: string;
  mEntityType = GroupType.DRIVEROBJECT;
  mEntityTypeName = "Driver Object";

  // All the Filter Options available
  filterOptionForDriverObjects: FilterOption[] = [];
  filterOptionForDriverObjectInstances: FilterOption[] = [];

  // The Filter Option Selected by the user
  selectedFilterOptionForDriverObject: FilterOption = FilterOption_RemoveFilter;
  selectedFilterOptionForDriverObjectInstance: FilterOption = FilterOption_RemoveFilter;

  // Filter value for the Filter Option being selected
  filterOptionValueForDriverObject: string = "";
  filterOptionValueForDriverObjectInstance: string = "";

  //Entity Instance Dialog Variables
  mInstance: DriverObjectInstance;
  mInstanceData: InstanceData[];
  mInstanceName: string;
  mInstanceEntityType: DriverObject;
  mInstanceType = GroupType.DRIVEROBJECTINSTANCE;
  mInstanceTypeName = "Driver Object";
  mAvailableEntityTypes: DriverObject[];

  currentPageForDriverObject = 1;
  currentPageForDriverObjectInstance = 1;

  totalNumberOfDriverObjects = 0;
  totalNumberOfDriverObjectInstances = 0;

  // List for Dropdown of Results Per Page value
  resultsPerPageValues = RESULTS_PER_PAGE;
  selectedPageSizeValueForTypes: number = this.resultsPerPageValues[0];
  selectedPageSizeValueForInstances: number = this.resultsPerPageValues[0];

  // Variables for the dropdown menu 
  functionList = [];
  selectedFunction = [];
  showContextMenu: boolean = false;
  contextMenuPosition = { x: 0, y: 0 };

  // Variable for General Confirm/Cancel Dialog Box
  showGeneralDialog: boolean = false;
  functionParameterData: FunctionParameterDataUi[] = [];

  constructor(
    private mRoute: ActivatedRoute,
    private mDriverObjectService: DriverObjectService,
    private mGroupService: GroupService,
    private cdr: ChangeDetectorRef,
    private spinnerService: NgxSpinnerService
  ) {}



  ngOnInit(): void {
    this.refreshFilterOptionsForDriverObjects();
    this.refreshFilterOptionsForDriverObjectInstances();
    this.selectedPageSizeValueForTypes = this.resultsPerPageValues[0];
    this.selectedPageSizeValueForInstances = this.resultsPerPageValues[0];

    this.mRoute.data.subscribe(
      (data: { 
        driverObjects: PagedResults<DriverObject>, 
        driverObjectInstances: PagedResults<DriverObjectInstance>}
      ) => {
        this.updatePagedDriverObject(data.driverObjects);
        this.updatePagedDriverObjectInstance(data.driverObjectInstances);
    });
  }


  isSingleDriverObjectSelected(): boolean {
    if (this.mDriverObjects != null && this.mDriverObjects.length > 0) {
      let oneSelected = false;
      for (const driverObject of this.mDriverObjects) {
        if (driverObject.mSelected === true) {
          if (oneSelected) {
            return false;
          } else {
            oneSelected = true;
          }
        }
      }
      return oneSelected;
    } else {
      return false;
    }
  }

  isAnyDriverObjectsSelected(): boolean {
    return this.mDriverObjects.some(driverObject => driverObject.mSelected);
  }

  isAllDriverObjectsSelected(): boolean {
    return this.mDriverObjects !== null && this.mDriverObjects.every(driverObject => driverObject.mSelected);
  }

  setAllDriverObjectsSelected(event: any): void {
    this.mDriverObjects.forEach(driverObject => driverObject.mSelected = event.target.checked);
  }





  isSingleDriverObjectInstanceSelected(): boolean {
    if (this.mDriverObjectInstances != null && this.mDriverObjectInstances.length > 0) {
      let oneSelected = false;
      for (const driverObjectInstance of this.mDriverObjectInstances) {
        if (driverObjectInstance.mSelected === true) {
          if (oneSelected) {
            return false;
          } else {
            oneSelected = true;
          }
        }
      }
      return oneSelected;
    } else {
      return false;
    }
  }

  isAnyDriverObjectInstancesSelected(): boolean {
    return this.mDriverObjectInstances.some(driverObjectInstance => driverObjectInstance.mSelected);
  }

  isAllDriverObjectInstancesSelected(): boolean {
    return this.mDriverObjectInstances !== null && this.mDriverObjectInstances.every(driverObjectInstance => driverObjectInstance.mSelected);
  }

  setAllDriverObjectInstancesSelected(event: any): void {
    this.mDriverObjectInstances.forEach(driverObjectInstance => driverObjectInstance.mSelected = event.target.checked);
  }





  getSelectedDriverObjects(): DriverObject[] {
    const driverObjects: DriverObject[] = [];
    this.mDriverObjects.filter(ui => ui.mSelected).forEach(ui => driverObjects.push(ui.mDriverObject));
    return driverObjects;
  }

  getSelectedDriverObjectIds(): number[] {
    const ids: number[] = [];
    this.mDriverObjects.filter(ui => ui.mSelected).forEach(ui => ids.push(ui.mDriverObject.id));
    return ids;
  }

  getSelectedDriverObjectNames(): string[] {
    const names: string[] = [];
    this.mDriverObjects.filter(ui => ui.mSelected).forEach(ui => names.push(ui.mDriverObject.name));
    return names;
  }





  getSelectedDriverObjectInstances(): DriverObjectInstance[] {
    const driverObjectInstances: DriverObjectInstance[] = [];
    this.mDriverObjectInstances.filter(ui => ui.mSelected).forEach(ui => driverObjectInstances.push(ui.mDriverObjectInstance));
    return driverObjectInstances;
  }

  getSelectedDriverObjectInstanceIds(): number[] {
    const ids: number[] = [];
    this.mDriverObjectInstances.filter(ui => ui.mSelected).forEach(ui => ids.push(ui.mDriverObjectInstance.id));
    return ids;
  }

  getSelectedDriverObjectInstanceNames(): string[] {
    const names: string[] = [];
    this.mDriverObjectInstances.filter(ui => ui.mSelected).forEach(ui => names.push(ui.mDriverObjectInstance.name));
    return names;
  }





  manageDriverObjectGroupsClicked(): void {
    this.mManageGroupsDialog.show(GroupType.DRIVEROBJECT);
  }

  manageDriverObjectInstanceGroupsClicked(): void {
    this.mManageGroupsDialog.show(GroupType.DRIVEROBJECTINSTANCE);
  }





  addSelectedDriverObjectsToGroupClicked(): void {
    if (this.isAnyDriverObjectsSelected()) {
      this.mAddToGroupDialogComponent.show(GroupType.DRIVEROBJECT, this.getSelectedDriverObjectNames());
    }
  }

  addSelectedDriverObjectInstancesToGroupClicked(): void {
    if (this.isAnyDriverObjectInstancesSelected()) {
      this.mAddToGroupDialogComponent.show(GroupType.DRIVEROBJECTINSTANCE, this.getSelectedDriverObjectInstanceNames());
    }
  }

  addSelectedToGroup(group: EmGroup): void {
    switch (group.type) {
      case GroupType.DRIVEROBJECT: {
        this
          .mGroupService
          .addItemsToGroup(group.id, this.getSelectedDriverObjectIds())
          .then(
            success => {
              this.refreshDriverObjects();
            },
            error => this.handleError(error)
          );
        break;
      }
      case GroupType.DRIVEROBJECTINSTANCE: {
        this
          .mGroupService
          .addItemsToGroup(group.id, this.getSelectedDriverObjectInstanceIds())
          .then(
            success => {
              this.refreshDriverObjectInstance();
            },
            error => this.handleError(error)
          );
        break;
      }
    }
  }





  removeSelectedDriverObjectsFromGroupClicked(): void {
    if (this.isAnyDriverObjectsSelected()) {
      const groupNames: string[] = [];
      const groups: EmGroup[] = [];
      const driverObjectNames: string[] = [];
      this.getSelectedDriverObjects().forEach(driverObject => {
        driverObjectNames.push(driverObject.name);
        if (driverObject.groups != null) {
          driverObject.groups.forEach(group => {
            if (!groupNames.includes(group.name)) {
              groupNames.push(group.name);
              groups.push(group);
            }
          });
        }
      });
      this.mRemoveFromGroupDialogComponent.show(groups, driverObjectNames);
    }
  }

  removeSelectedDriverObjectInstancesFromGroupClicked(): void {
    if (this.isAnyDriverObjectInstancesSelected()) {
      const groupNames: string[] = [];
      const groups: EmGroup[] = [];
      const driverObjectInstanceNames: string[] = [];
      this.getSelectedDriverObjectInstances().forEach(driverObjectInstance => {
        driverObjectInstanceNames.push(driverObjectInstance.name);
        if (driverObjectInstance.groups != null) {
          driverObjectInstance.groups.forEach(group => {
            if (!groupNames.includes(group.name)) {
              groupNames.push(group.name);
              groups.push(group);
            }
          });
        }
      });
      this.mRemoveFromGroupDialogComponent.show(groups, driverObjectInstanceNames);
    }
  }

  removeSelectedFromGroup(group: EmGroup): void {
    switch (group.type) {
      case GroupType.DRIVEROBJECT: {
        this
          .mGroupService
          .removeItemsFromGroup(group.id, this.getSelectedDriverObjectIds())
          .then(
            success => {
              this.refreshDriverObjects();
            },
            error => this.handleError(error)
          );
        break;
      }
      case GroupType.DRIVEROBJECTINSTANCE: {
        this
          .mGroupService
          .removeItemsFromGroup(group.id, this.getSelectedDriverObjectInstanceIds())
          .then(
            success => {
              this.refreshDriverObjectInstance();
            },
            error => this.handleError(error)
          );
        break;
      }
    }
  }


  showGroup(group: EmGroup): void {
    switch (group.type) {
      case GroupType.DRIVEROBJECT: {
        this
          .mGroupService
          .getAllDriverObjectsInGroup(group.id)
          .then(
            success => {
              const itemNames: string[] = [];
              success.forEach(member => itemNames.push(member.name));
              this.mShowGroupDialog.show(group.name, itemNames);
            },
            error => this.handleError(error)
          );
        break;
      }
      case GroupType.DRIVEROBJECTINSTANCE: {
        this
          .mGroupService
          .getAllDriverObjectInstancesInGroup(group.id)
          .then(
            success => {
              const itemNames: string[] = [];
              success.forEach(member => itemNames.push(member.name));
              this.mShowGroupDialog.show(group.name, itemNames);
            },
            error => this.handleError(error)
          );
        break;
      }
    }
  }





  addDriverObjectClicked(): void {
    this.mEntity = new DriverObject();
    this.mEntityName = null;
    this.mEntityTypeDialogComponent.mParents = this.mAvailableEntityTypes;
    this.mEntityTypeDialogComponent.entityParent = null;
    this.mEntityTypeDialogComponent.showDialog();
  }

  editSelectedDriverObjectClicked(): void {
    if (this.isSingleDriverObjectSelected()) {
      this.editDriverObject(this.getSelectedDriverObjects()[0]);
    }
  }

  duplicateSelectedDriverObjectClicked(): void {
    if (this.isSingleDriverObjectSelected()) {
      this
        .mDriverObjectService
        .getDriverObjectById(this.getSelectedDriverObjects()[0].id)
        .then(
          response => {
            response.id = null;
            if (response.properties != null) {
              response.properties.forEach(p => p.id = null);
            }

            this.mEntity = response;
            this.mEntityName = null;
            this.mEntityTypeDialogComponent.showDialog();
          },
          error => this.handleError(error)
        );
    }
  }

  deleteSelectedDriverObjectsClicked(): void {
    if (this.isAnyDriverObjectsSelected()) {
      this.mDeleteFlag = 1; //1 = Driver Objects
      this.mConfirmDeleteDialog.show("Driver Object Type", this.getSelectedDriverObjectNames());
    }
  }

  editDriverObject(driverObject: DriverObject): void {
    this
      .mDriverObjectService
      .getDriverObjectById(driverObject.id)
      .then(
        response => {
          this.mEntity = response;
          this.mEntityName = response.name;
          this.mEntityTypeDialogComponent.entityParent = response.parent;
          this.mEntityTypeDialogComponent.mParents = this.mAvailableEntityTypes;

          if (response.parent != null) {
            this.mEntityTypeDialogComponent.entityParent = this.mAvailableEntityTypes.find(p => p.id == response.parent.id);
          }

          this.mEntityTypeDialogComponent.showDialog();
        },
        error => this.handleError(error)
      );
  }

  createOrUpdateEntityType(): void {
    if (this.mEntity.id == null) {
      //ID = null, this is a new Driver Object... Create it
      this
        .mDriverObjectService
        .createDriverObject(this.mEntity)
        .then(
          response => {
            this.refreshDriverObjects();
            this.mEntityTypeDialogComponent.hideDialog();
          },
          error => this.handleError(error)
        );
    } else {
      //ID <> null, this is an existing Driver Object... Update it
      this
        .mDriverObjectService
        .updateDriverObject(this.mEntity)
        .then(
          response => {
            this.refreshDriverObjects();
            this.mEntityTypeDialogComponent.hideDialog();
          },
          error => this.handleError(error)
        );
    }
  }


  addDriverObjectInstanceClicked(): void {
    //Create a new Instance to be modeled in the Entity Instance Dialog
    this.mInstance = new DriverObjectInstance();
    this.mInstanceName = null;

    //Refresh the Availabe Types, Reload the Instance Data and Show the Dialog to start editing
    this.mEntityInstanceDialogComponent.setOriginalInstanceData([]);
    this.refreshTypesReloadDataAndShowDialog();
  }

  editSelectedDriverObjectInstanceClicked(): void {
    if (this.isSingleDriverObjectInstanceSelected()) {
      this.editDriverObjectInstance(this.getSelectedDriverObjectInstances()[0]);
    }
  }

  duplicateSelectedDriverObjectInstanceClicked(): void {
    if (this.isSingleDriverObjectInstanceSelected()) {
      this
      .mDriverObjectService
      .getDriverObjectInstanceById(this.getSelectedDriverObjectInstances()[0].id)
      .then(
        response => {
          //Set the Instance to be modeled in the Entity Instance Dialog to the Instance returned...
          this.mInstance = response;

          //Set the Instance Name value so that it gets properly displayed in the Dialog Header
          this.mInstanceName = response.name;

          //Reset the IDs of the Instance and all the Instance Data so that it gets treated as new on the Server
          this.mInstance.id = null;
          this.mInstance.uuid = null;
          if (this.mInstance.data != null) {
            this.mInstance.data.forEach(d => d.id = null);
          }

          //Refresh the Availabe Types, Reload the Instance Data and Show the Dialog to start editing
          this.refreshTypesReloadDataAndShowDialog();
        },
        error => this.handleError(error)
      );
    }
  }

  deleteSelectedDriverObjectInstancesClicked(): void {
    if (this.isAnyDriverObjectInstancesSelected()) {
      this.mDeleteFlag = 2; //2 = Driver Object Instances
      this.mConfirmDeleteDialog.show("Driver Object", this.getSelectedDriverObjectInstanceNames());
    }
  }

  editDriverObjectInstance(serverObjectInstance: DriverObjectInstance): void {
    this
      .mDriverObjectService
      .getDriverObjectInstanceById(serverObjectInstance.id)
      .then(
        response => {
          console.log("Received DriverObjectInstance");

          //Set the Instance being modeled in the Entity Instance Dialog to the Instance that was returned
          this.mInstance = response;

          //Set the Instance Name which is displayed in the Dialog Header
          this.mInstanceName = response.name;

          // Set the original Instance Data being displayed by the EntityInstanceDialogComponent
          this.mEntityInstanceDialogComponent.setOriginalInstanceData(this.mInstance.data);

          //Refresh the Available Types, Reload the Instance Data and Show the Dialog to start editing
          this.refreshTypesReloadDataAndShowDialog();
        },
        error => this.handleError(error)
      );
  }

  async refreshTypesReloadDataAndShowDialog(): Promise<void> {
    //Create a List of the available Driver Objects that the Instance can be a type of
    this.mAvailableEntityTypes = await this.mDriverObjectService.getAllDriverObjects();

    //Find/Set the Entity Type the Instance is the type of
    this.mInstanceEntityType = this.mInstance.driverObject == null? null : this.mAvailableEntityTypes.find(entityType => entityType.id == this.mInstance.driverObject.id);
    if (this.mInstanceEntityType != null) {
      this.mInstanceEntityType.properties = this.mInstance.driverObject.properties;
    }

    // Need to detect the changes here so that the "mInstanceEntityType" reference update gets passed to the EntityInstanceDialogComponent being shown below
    this.cdr.detectChanges();

    //Refresh the Instance Data, loading the existing values in for the Instance being edited
    this.mEntityInstanceDialogComponent.refreshData();

    //Show the Entity Instance Dialog
    this.mEntityInstanceDialogComponent.showDialog();
  }

  createOrUpdateEntityInstance(): void {
    //Set the values to the Driver Object Instance to be created
    this.mInstance.driverObject = this.mInstanceEntityType;

    if (this.mInstance.id == null) {
      //ID is null, Driver Object Instance does not exist!  Create it...
      this
        .mDriverObjectService
        .createDriverObjectInstance(this.mInstance)
        .then(
          response => {
            this.refreshDriverObjectInstance();
            this.mEntityInstanceDialogComponent.hideDialog();
          },
          error => this.handleError(error)
        );
    } else {
      //ID is not null, Driver Object Instance exists!  Update it...
      this
        .mDriverObjectService
        .updateDriverObjectInstance(this.mInstance)
        .then(
          response => {
            this.refreshDriverObjectInstance();
            this.mEntityInstanceDialogComponent.hideDialog();
          },
          error => this.handleError(error)
        );
    }
  }

  selectedEntityTypeChanged(entityType: DriverObject): void {
    this.mInstanceEntityType = entityType;
    if (this.mInstanceEntityType != null && this.mInstanceEntityType.properties == null) {
      this
        .mDriverObjectService
        .getDriverObjectById(this.mInstanceEntityType.id)
        .then(
          foundEntityType => {
            this.mInstanceEntityType.properties = foundEntityType.properties;
            this.mEntityInstanceDialogComponent.refreshData();
          },
          error => this.handleError(error)
        );
    } else {
      this.mEntityInstanceDialogComponent.refreshData();
    }
  }


  deleteConfirmed(): void {
    if (this.mDeleteFlag == 1) {  //1 = Driver Objects
      this
        .mDriverObjectService
        .deleteDriverObjects(this.getSelectedDriverObjectIds())
        .then(
          response => {
            this.refreshDriverObjects();
          },
          error => this.handleError(error)
        );
    } else if (this.mDeleteFlag == 2) { //2 = Driver Object Instances
      this
        .mDriverObjectService
        .deleteDriverObjectInstances(this.getSelectedDriverObjectInstanceIds())
        .then(
          response => {
            this.refreshDriverObjectInstance();
          },
          error => this.handleError(error)
        );
    }
  }



  /******************** FILTER FUNCTIONS FOR DRIVER OBJECTS *****************/

  private refreshFilterOptionsForDriverObjects(): void {
    while(this.filterOptionForDriverObjects.length > 0) {
      this.filterOptionForDriverObjects.pop();
    }

    this.filterOptionForDriverObjects.push(FilterOption_RemoveFilter);
    this.filterOptionForDriverObjects.push({ column: "name", label: "Name"} as FilterOption);
  }

  onFilterOptionForDriverObjectClicked(filterOption: FilterOption): void {
    if (this.selectedFilterOptionForDriverObject == filterOption) {
      return;
    }

    this.selectedFilterOptionForDriverObject = filterOption;
    this.filterOptionValueForDriverObject = "";
    this.currentPageForDriverObject = 1;
    this.refreshDriverObjects();

  }

  onFilterValueForDriverObjectChanged(): void {
    this.currentPageForDriverObject = 1;
    this.refreshDriverObjects();
  }

  filterStringChangedDriverObjects(pageNumber?: number): void {
    if (!pageNumber) { pageNumber = 1; }
    // Show only size on this new page
    console.log("page Changed to " + pageNumber);
    this.currentPageForDriverObject = pageNumber;

    this.pageChangedDriverObject(pageNumber);

  }


    /******************** FILTER FUNCTIONS FOR DRIVER OBJECTS INSTANCES *****************/

  private refreshFilterOptionsForDriverObjectInstances(): void {
    while(this.filterOptionForDriverObjectInstances.length > 0) {
      this.filterOptionForDriverObjectInstances.pop();
    }

    this.filterOptionForDriverObjectInstances.push(FilterOption_RemoveFilter);
    this.filterOptionForDriverObjectInstances.push({ column: "name", label: "Name"} as FilterOption);
  }
  
  
  onFilterOptionForDriverObjectInstanceClicked(filterOption: FilterOption): void {
    if (this.selectedFilterOptionForDriverObjectInstance == filterOption) {
      return;
    }

    this.selectedFilterOptionForDriverObjectInstance = filterOption;
    this.filterOptionValueForDriverObjectInstance = "";
    this.currentPageForDriverObject = 1;
    this.refreshDriverObjects();

  }

  onFilterValueForDriverObjectInstanceChanged(): void {
    this.currentPageForDriverObjectInstance = 1;
    this.refreshDriverObjectInstance();
  }

  filterStringChangedDriverObjectInstances(pageNumber?: number): void {
    if (!pageNumber) { pageNumber = 1; }
    // Show only size on this new page
    console.log("page Changed to " + pageNumber);
    this.currentPageForDriverObjectInstance = pageNumber;

    this.pageChangedDriverObjectInstance(pageNumber);

  }

  refreshInstanceData(): void {
    this
      .mDriverObjectService
      .getDriverObjectInstanceById(this.mInstance.id)
      .then(
        response => {
          this.mInstance.data = response.data;
          this.mEntityInstanceDialogComponent.setOriginalInstanceData(response.data);
          this.mEntityInstanceDialogComponent.refreshData();
        },
        error => this.handleError(error)
      );
  }

  handleError(error: any): void {
    /* New handling (TJB 04/18/2023) */
    if (error?.error != null) {
      this.simpleSuccessErrorDialogComponent.showErrorDialog(ERROR_DIALOG_TITLE, error.error);
    } 
    
    /* Default back to old handling */
    else if (error != null) {
      const response: Response = error;
      alert(response.text());
    }
  }


  /******************** PAGINATOR FUNCTIONS FOR DRIVER OBJECT  *****************/

  pageChangedDriverObject(pageNumber: number): void {
    if (pageNumber == null || pageNumber == undefined || pageNumber < 1) {
      return;
    }

    if(this.currentPageForDriverObject == pageNumber) {
      return;
    }

    this.currentPageForDriverObject = pageNumber;
    this.refreshDriverObjects();
  }


  private async refreshDriverObjects(): Promise<void> {
    this.spinnerService.show();
    let pagedData: PagedResults<DriverObject>;
    try {
      pagedData = await this.mDriverObjectService.getAllDriverObjectsForPaging((this.currentPageForDriverObject - 1) * this.selectedPageSizeValueForTypes, this.selectedPageSizeValueForTypes, "ASC", this.filterOptionValueForDriverObject);
    } catch (error) {
      this.spinnerService.hide();
      this.handleError(error);
      return;
    }

    this.updatePagedDriverObject(pagedData);
    this.spinnerService.hide();
  }


  private updatePagedDriverObject(pagedData: PagedResults<DriverObject>): void {
    const uiObjects: DriverObjectUI[] = [];
    if(pagedData.data != null) {
      pagedData.data.forEach(driverObject => {
        const ui = new DriverObjectUI();
        ui.mDriverObject = driverObject;
        ui.mSelected = false;
        uiObjects.push(ui);
      })
    }
    this.mDriverObjects = uiObjects;

    this.totalNumberOfDriverObjects = pagedData.size;
  }


  /******************** PAGINATOR FUNCTIONS FOR DRIVER OBJECT INSTANCES *****************/

  pageChangedDriverObjectInstance(pageNumber: number): void {
    if (pageNumber == null || pageNumber == undefined || pageNumber < 1) {
      return;
    }

    if(this.currentPageForDriverObjectInstance == pageNumber) {
      return;
    }

    this.currentPageForDriverObjectInstance = pageNumber;
    this.refreshDriverObjectInstance();
  }

  private async refreshDriverObjectInstance(): Promise<void> {
    this.spinnerService.show();
    let pagedData: PagedResults<DriverObjectInstance>;
    try {
      pagedData = await this.mDriverObjectService.getAllDriverObjectInstancesForPaging((this.currentPageForDriverObjectInstance - 1) * this.selectedPageSizeValueForInstances, this.selectedPageSizeValueForInstances, "ASC", this.filterOptionValueForDriverObjectInstance);
    } catch (error) {
      this.spinnerService.hide();
      this.handleError(error);
      return;
    }
    
    this.updatePagedDriverObjectInstance(pagedData);
    this.spinnerService.hide();
  }

  private updatePagedDriverObjectInstance(pagedData: PagedResults<DriverObjectInstance>): void {
    const uiObjects: DriverObjectInstanceUI[] = [];
    if(pagedData.data != null) {
      pagedData.data.forEach(driverObjectInstances => {
        const ui = new DriverObjectInstanceUI();
        ui.mDriverObjectInstance = driverObjectInstances;
        ui.mSelected = false;
        uiObjects.push(ui);
      })
    }

    this.mDriverObjectInstances = uiObjects;
    this.totalNumberOfDriverObjectInstances = pagedData.size;
  }

   /******************** RESULTS PER PAGE FUNCTIONS  *****************/

  manageResultPerPageClickedForTypes(value: number): void{
    this.selectedPageSizeValueForTypes = value;
    this.currentPageForDriverObject = 1;
    this.refreshDriverObjects();
  }

  manageResultPerPageClickedForInstances(value: number): void {
    this.selectedPageSizeValueForInstances = value;
    this.currentPageForDriverObjectInstance = 1;
    this.refreshDriverObjectInstance();
  }

 /*  
  *  Handling the Right click event only on Driver-Object Instance Name Column
  *  No function is available for Driver Object 
*/
  fetchFunctionList(event: MouseEvent, driverObjectInstance: DriverObjectInstance): void {
    // Stop the default handling of right click event
    event.preventDefault();
    this.showContextMenu = true;
    this.showGeneralDialog = false;

    // Set the x & y co-ordinates for the context menu to open up
    this.contextMenuPosition.x = event.clientX;
    this.contextMenuPosition.y = event.clientY;

    // Empty the function List if any element still present
    this.functionList = [];

    // Send the request to get the driver-objects Instance details using the ID
    this
      .mDriverObjectService
      .getDriverObjectInstanceById(driverObjectInstance.id)
      .then(
        response => {
          //Set the Instance being modeled in the drop-down to the Instance that was returned
          this.mInstance = response;

          // Create the function List for the context menu from the response received 
          try {
            if (response.driverObject && Array.isArray(response.driverObject) && (response.driverObject).length > 0) {
              this.functionList = response.driverObject;

            } else {
              // Input a dummy

              this.functionList = INFRA_DUMMY_FUNCTION;
            }
          }
          catch (error) {
            this.handleError(error);
          }
        },
        error => this.handleError(error)
      );
     

  }

  onItemClicked(item: any) {
    // Handle the click in the dropdown menu item here 
    this.selectedFunction = item;

    // If its a dummy then do not open the Dialog Box
    if (item.id != -1) {
      // Display the Dialog Box for confirmation
      this.showGeneralDialog = true;
    }

    // Hide the Context Menu/Dropdown
    this.showContextMenu = false;

  }


}

class DriverObjectUI {
  mSelected = false;
  mDriverObject: DriverObject;
}

class DriverObjectInstanceUI {
  mSelected = false;
  mDriverObjectInstance: DriverObjectInstance;
}

// Filter Option present for the user
interface FilterOption {
  column: string;
  label: string;
}